import { getLiveShareCopy } from '../i18n.js';
import { decodeJsonPayload } from '../utils/base64.js';
import { labelForEventSubtype, labelForEventType } from '../utils/eventTypeLabels.js';

const html = String.raw;

const formatTemplate = (template, replacements = {}) => {
  if (!template) return '';
  return template.replace(/{{(.*?)}}/g, (_, key) => {
    const trimmed = key.trim();
    const value = replacements[trimmed];
    return value === undefined || value === null ? '' : value;
  });
};

const renderRequirementCard = (item, copy) => html`
  <div class="card live-panel stack tight">
    <strong>${formatEventLabel(item, copy)}</strong>
    <p>${item.text ?? ''}</p>
  </div>
`;

const formatEventLabel = (item, copy) => {
  const typeLabel = labelForEventType(item.eventType ?? item.type, copy.requirementsList?.types ?? copy.types ?? {});
  const subtypeLabel = labelForEventSubtype(item.eventSubtype, copy.requirementsList?.types ?? copy.types ?? {});
  return subtypeLabel ? `${typeLabel} · ${subtypeLabel}` : typeLabel;
};

const downloadFile = (filename, content, mime = 'text/plain') => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const renderLiveShareView = (container, encodedPayload) => {
  const copy = getLiveShareCopy();
  let payload = null;
  try {
    payload = decodeJsonPayload(encodedPayload);
  } catch (error) {
    console.error('Failed to decode shared payload', error);
  }
  if (!payload) {
    container.innerHTML = html`
      <div class="card stack">
        <h2>${copy.title}</h2>
        <p class="muted">${copy.loadError}</p>
      </div>
    `;
    return;
  }

  const createdAt = payload.createdAt ? new Date(payload.createdAt).toLocaleString() : copy.sharedBy.unknown;
  const requirements = Array.isArray(payload.requirements) ? payload.requirements : [];
  const sharedBy = payload.sharedBy ?? null;
  const sharedByLine = (() => {
    if (!sharedBy) return null;
    const name = sharedBy.name?.trim() || '';
    const email = sharedBy.email?.trim() || '';
    const company = sharedBy.company?.trim() || '';
    let line = `${copy.sharedBy.prefix} `;
    if (name && email) {
      line += `${name} (${email})`;
    } else if (name) {
      line += name;
    } else if (email) {
      line += email;
    } else {
      line += copy.sharedBy.unknown;
    }
    if (company) {
      line += `${copy.sharedBy.companySeparator}${company}`;
    }
    return line;
  })();

  const sessionLabel = formatTemplate(copy.sessionLabel, { date: createdAt });

  container.innerHTML = html`
    <div class="card stack">
      <h2>${copy.title}</h2>
      <p class="muted">${sessionLabel}</p>
      ${sharedByLine ? `<p class="muted">${sharedByLine}</p>` : ''}
      <div class="row">
        <button type="button" id="share-download-csv">${copy.buttons.csv}</button>
        <button type="button" id="share-download-md" class="ghost-button">${copy.buttons.md}</button>
      </div>
      <div class="stack tight">
        ${
          requirements.length
            ? requirements.map((req) => renderRequirementCard(req, copy)).join('')
            : `<p class="muted">${copy.empty}</p>`
        }
      </div>
      <p class="muted small">${copy.note}</p>
    </div>
  `;

  const csvButton = container.querySelector('#share-download-csv');
  const mdButton = container.querySelector('#share-download-md');

  csvButton?.addEventListener('click', () => {
    const rows = [[copy.download.csvHeaders.type, copy.download.csvHeaders.text]].concat(
      requirements.map((req) => [
        formatEventLabel(req, copy),
        req.text ?? '',
      ]),
    );
    const csvContent = rows
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    downloadFile('anforderungen.csv', csvContent, 'text/csv');
  });

  mdButton?.addEventListener('click', () => {
    const lines = [`# ${copy.download.markdown.title}`];
    if (sharedByLine) {
      lines.push(`> ${sharedByLine}`);
    }
    lines.push(`> ${copy.download.markdown.sessionLabel}: ${createdAt}`);
    lines.push('');
    requirements.forEach((req, index) => {
      lines.push(`## ${index + 1}. ${formatEventLabel(req, copy)}`);
      lines.push(req.text ?? '');
      lines.push('');
    });
    downloadFile('anforderungen.md', lines.join('\n'), 'text/markdown');
  });
};
